import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError, API } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, PageBody } from "@/components/PageChrome";
import { Button } from "@/components/controls";
import PaginationBar from "@/components/PaginationBar";
import { MessageSquare, Send, Paperclip, X, Search, Plus, Download, Languages, RefreshCw, Pencil, Trash2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { T } from "@/lib/testIds";
import { translateText, detectLang, LANG_FLAG } from "@/lib/translate";

function initials(u) {
  const s = (u?.name || u?.email || "?").trim();
  return s.slice(0, 2).toUpperCase();
}

function fmtTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    return sameDay ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : d.toLocaleString();
  } catch (_e) { return iso; }
}

export default function Chat() {
  const { user, updatePreferences } = useAuth();
  const [threads, setThreads] = useState([]);
  const [users, setUsers] = useState([]);
  const [activeTid, setActiveTid] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [attaching, setAttaching] = useState(false);
  const [pending, setPending] = useState(null); // {url, name}
  const [showNew, setShowNew] = useState(false);
  const [userQ, setUserQ] = useState("");
  // {msgId: {translated, showing}} — client-side translation cache per message
  const [msgXlate, setMsgXlate] = useState({});
  const [xlatingId, setXlatingId] = useState(null);
  // id of the message open in the inline editor (own messages only)
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [composeXlating, setComposeXlating] = useState(false);
  const [threadPage, setThreadPage] = useState(1);
  const [threadPageSize, setThreadPageSize] = useState(() => Number(localStorage.getItem("chat_threads_page_size")) || 25);
  const [threadTotal, setThreadTotal] = useState(0);
  const scrollRef = useRef(null);
  const fileRef = useRef(null);

  const targetLang = (user?.preferred_language || "en").toLowerCase();
  const autoTranslate = !!user?.auto_translate_chat;

  const loadThreads = async () => {
    try {
      const { data } = await api.get("/chat/threads", { params: { paginated: 1, page: threadPage, page_size: threadPageSize } });
      const items = Array.isArray(data) ? data : (data.items || []);
      setThreads(items);
      setThreadTotal(Array.isArray(data) ? items.length : (data.total ?? 0));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const loadUsers = async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data.filter((u) => u.id !== user?.id));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const loadMessages = async (tid) => {
    try {
      const { data } = await api.get(`/chat/threads/${tid}/messages`);
      setMessages(data);
      setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight }), 60);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { loadThreads(); loadUsers(); }, []); // eslint-disable-line
  useEffect(() => { loadThreads(); /* eslint-disable-next-line */ }, [threadPage, threadPageSize]);
  useEffect(() => { if (activeTid) loadMessages(activeTid); }, [activeTid]);

  const startEdit = (m) => {
    setEditingId(m.id);
    setEditDraft(m.text || "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft("");
  };

  const saveEdit = async (m) => {
    const body = editDraft.trim();
    if (!body && !m.attachment_url) {
      toast.error("Message cannot be empty");
      return;
    }
    setSavingEdit(true);
    try {
      await api.patch(`/chat/threads/${activeTid}/messages/${m.id}`, { text: body });
      cancelEdit();
      await loadMessages(activeTid);
      // the thread list shows a preview of the last message, so refresh it too
      await loadThreads();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingEdit(false);
    }
  };

  const removeMessage = async (m) => {
    if (!window.confirm("Delete this message for everyone in the chat?")) return;
    try {
      await api.delete(`/chat/threads/${activeTid}/messages/${m.id}`);
      if (editingId === m.id) cancelEdit();
      await loadMessages(activeTid);
      await loadThreads();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // Poll for new messages every 6s
  useEffect(() => {
    if (!activeTid) return;
    const id = setInterval(() => loadMessages(activeTid), 6000);
    return () => clearInterval(id);
  }, [activeTid]); // eslint-disable-line

  // Auto-translate messages that don't match the user's preferred language.
  useEffect(() => {
    if (!autoTranslate || !messages.length) return;
    let cancelled = false;
    const run = async () => {
      for (const m of messages) {
        if (cancelled) return;
        if (!m.text || m.is_bot || m.sender_id === "__ai_bot__") continue;
        if (detectLang(m.text) === targetLang) continue;
        if (msgXlate[m.id]?.translated) continue;
        try {
          const r = await translateText(m.text, targetLang);
          if (cancelled) return;
          setMsgXlate((prev) => ({ ...prev, [m.id]: { translated: r.translated_text, showing: true } }));
        } catch (_e) { /* silent */ }
      }
    };
    run();
    return () => { cancelled = true; };
  }, [messages, autoTranslate, targetLang]); // eslint-disable-line

  const translateMessage = async (m) => {
    if (msgXlate[m.id]?.translated) {
      setMsgXlate((prev) => ({ ...prev, [m.id]: { ...prev[m.id], showing: !prev[m.id].showing } }));
      return;
    }
    setXlatingId(m.id);
    try {
      const r = await translateText(m.text, targetLang);
      setMsgXlate((prev) => ({ ...prev, [m.id]: { translated: r.translated_text, showing: true } }));
    } catch (_e) {
      toast.error("Translation failed");
    }
    setXlatingId(null);
  };

  const setLanguage = async (lang) => {
    try {
      await updatePreferences({ preferred_language: lang });
      setMsgXlate({}); // reset local translations for new target
      toast.success(`Language set to ${LANG_FLAG[lang]}`);
    } catch (_e) { /* toasted upstream */ }
  };
  const toggleAutoTranslate = async () => {
    try {
      await updatePreferences({ auto_translate_chat: !autoTranslate });
    } catch (_e) { /* toasted upstream */ }
  };

  const composeTranslate = async () => {
    const src = text.trim();
    if (!src) return;
    const srcLang = detectLang(src);
    // Toggle target: if user has zh preference, translate EN→ZH; else ZH→EN.
    const composeTarget = srcLang === "en" ? "zh" : "en";
    setComposeXlating(true);
    try {
      const r = await translateText(src, composeTarget);
      if (r.translated_text && r.translated_text !== src) {
        setText(r.translated_text);
        toast.success(`Translated → ${LANG_FLAG[composeTarget]}`);
      } else {
        toast.info("Nothing to translate");
      }
    } catch (_e) {
      toast.error("Translation failed — original text kept");
    }
    setComposeXlating(false);
  };

  const openWith = async (other) => {
    try {
      const { data } = await api.post("/chat/threads", { user_id: other.id });
      setActiveTid(data.id);
      setShowNew(false);
      await loadThreads();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const send = async () => {
    if (!activeTid) return;
    if (!text.trim() && !pending) return;
    const raw = text.trim();
    try {
      await api.post(`/chat/threads/${activeTid}/messages`, {
        text: raw,
        attachment_url: pending?.url,
        attachment_name: pending?.name,
      });
      setText("");
      setPending(null);
      await loadMessages(activeTid);
      loadThreads();
      // Intercept @commands: after posting the user message, ask the AI
      // service to interpret. If it recognises the command, inject a local
      // bot reply (ephemeral — visible only to the sender).
      if (raw.startsWith("@")) {
        try {
          const { data } = await api.post("/ai/chat-command", { text: raw });
          if (data.is_command && data.reply) {
            setMessages((prev) => [...prev, {
              id: `bot-${Date.now()}`,
              sender_id: "__ai_bot__",
              text: data.reply,
              created_at: new Date().toISOString(),
              is_bot: true,
            }]);
            setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight }), 60);
          }
        } catch (_) { /* silent — bot is optional */ }
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onAttach = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setAttaching(true);
    try {
      const form = new FormData(); form.append("file", f);
      const { data } = await api.post("/files/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      setPending({ url: data.url, name: data.original_filename });
    } catch (err) { toast.error(formatApiError(err)); }
    setAttaching(false);
    e.target.value = "";
  };

  const activeThread = threads.find((t) => t.id === activeTid);
  const filteredUsers = users.filter((u) => {
    const q = userQ.toLowerCase();
    return !q || (u.email || "").toLowerCase().includes(q) || (u.name || "").toLowerCase().includes(q);
  });
  const attachmentSrc = (url) => (url?.startsWith("/api/files/") ? `${API.replace(/\/api$/, "")}${url}` : url);
  const isImage = (name) => /\.(png|jpe?g|gif|webp)$/i.test(name || "");

  return (
    <>
      <PageHeader title="Chat" subtitle="Direct messages with attachments" testId={T.moduleTitle("chat")} module="chat_threads">
        <div className="flex items-center gap-1.5">
          <div className="inline-flex border border-slate-200 bg-white rounded-sm overflow-hidden" data-testid="chat-lang-switcher">
            {["en", "zh"].map((l) => (
              <button
                key={l}
                onClick={() => setLanguage(l)}
                data-testid={`chat-lang-${l}`}
                className={`h-8 px-2 text-[11.5px] font-semibold ${targetLang === l ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-50"}`}
                title={l === "en" ? "English" : "中文 (Chinese)"}
              >
                {LANG_FLAG[l]}
              </button>
            ))}
          </div>
          <button
            onClick={toggleAutoTranslate}
            data-testid="chat-auto-translate-toggle"
            className={`h-8 px-2 inline-flex items-center gap-1 border rounded-sm text-[11.5px] font-semibold ${autoTranslate ? "bg-indigo-600 text-white border-indigo-600" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}
            title="Auto-translate incoming messages"
          >
            <Languages size={12} /> {autoTranslate ? "Auto-translate ON" : "Auto-translate"}
          </button>
          <Button variant="primary" onClick={() => setShowNew(true)} testId="chat-new-btn">
            <Plus size={13} /> New chat
          </Button>
        </div>
      </PageHeader>
      <PageBody>
        <div className="grid grid-cols-[280px_1fr] gap-3 h-[calc(100vh-180px)] min-h-[500px]">
          {/* Threads sidebar */}
          <div className="border border-slate-200 bg-white rounded-sm overflow-hidden flex flex-col">
            <div className="h-10 border-b border-slate-200 px-3 flex items-center text-[11.5px] font-semibold uppercase tracking-wider text-slate-600">
              Conversations <span className="ml-auto font-mono text-[10.5px] text-slate-400">{threadTotal}</span>
            </div>
            <div className="flex-1 overflow-auto">
              {threads.length === 0 && (
                <div className="p-4 text-[12px] text-slate-500">No conversations yet. Click &quot;New chat&quot; to start.</div>
              )}
              {threads.map((t) => {
                const other = t.other_user;
                const active = t.id === activeTid;
                return (
                  <button
                    key={t.id}
                    data-testid={`chat-thread-${t.id}`}
                    onClick={() => setActiveTid(t.id)}
                    className={`w-full text-left px-3 py-2 border-l-2 flex gap-2 items-start ${active ? "border-slate-900 bg-slate-50" : "border-transparent hover:bg-slate-50"}`}
                  >
                    <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-800 text-[11px] font-bold flex items-center justify-center shrink-0">
                      {initials(other)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[12.5px] font-medium text-slate-900 truncate">{other?.name || other?.email || "Unknown"}</div>
                      <div className="text-[11px] text-slate-500 truncate">{t.last_message?.text || <span className="italic text-slate-400">No messages yet</span>}</div>
                    </div>
                    <div className="text-[10px] text-slate-400 font-mono">{fmtTime(t.updated_at)}</div>
                  </button>
                );
              })}
            </div>
            {threadTotal > threadPageSize && (
              <div className="border-t border-slate-200 px-2 py-1.5 flex items-center justify-center">
                <PaginationBar
                  page={threadPage}
                  pageSize={threadPageSize}
                  total={threadTotal}
                  onPageChange={setThreadPage}
                  onPageSizeChange={(n) => { localStorage.setItem("chat_threads_page_size", String(n)); setThreadPageSize(n); setThreadPage(1); }}
                  pageSizeOptions={[10, 25, 50, 100]}
                  testIdPrefix="chat-threads"
                />
              </div>
            )}
          </div>

          {/* Message pane */}
          <div className="border border-slate-200 bg-white rounded-sm overflow-hidden flex flex-col">
            {!activeTid && (
              <div className="flex-1 flex flex-col items-center justify-center text-slate-400">
                <MessageSquare size={40} strokeWidth={1.5} />
                <div className="mt-3 text-[13px]">Select a conversation to begin</div>
              </div>
            )}
            {activeTid && (
              <>
                <div className="h-12 border-b border-slate-200 px-4 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-800 text-[11px] font-bold flex items-center justify-center">
                    {initials(activeThread?.other_user)}
                  </div>
                  <div>
                    <div className="text-[13px] font-semibold text-slate-900">{activeThread?.other_user?.name || activeThread?.other_user?.email}</div>
                    <div className="text-[10.5px] text-slate-500">{activeThread?.other_user?.email}</div>
                  </div>
                </div>
                <div ref={scrollRef} className="flex-1 overflow-auto p-4 space-y-2 bg-slate-50/40">
                  {messages.map((m) => {
                    const mine = m.sender_id === user?.id;
                    const isBot = m.is_bot || m.sender_id === "__ai_bot__";
                    if (isBot) {
                      return (
                        <div key={m.id} className="flex justify-center">
                          <div className="max-w-[80%] rounded-sm px-3 py-2 text-[12.5px] bg-gradient-to-br from-indigo-50 to-violet-50 border border-indigo-200 text-slate-800">
                            <div className="flex items-center gap-1.5 mb-1 text-[10.5px] uppercase tracking-wider text-indigo-700 font-semibold">
                              🤖 ImportAI
                              <span className="text-slate-400 text-[9.5px] font-normal normal-case tracking-normal">(visible only to you)</span>
                            </div>
                            <div className="whitespace-pre-wrap break-words">{m.text}</div>
                          </div>
                        </div>
                      );
                    }
                    return (
                      <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[70%] rounded-sm px-3 py-2 text-[13px] border ${mine ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-900 border-slate-200"}`}>
                          {m.attachment_url && (
                            <div className={`mb-1.5 ${mine ? "text-slate-100" : "text-slate-700"}`}>
                              {isImage(m.attachment_name) ? (
                                <a href={attachmentSrc(m.attachment_url)} target="_blank" rel="noreferrer">
                                  <img referrerPolicy="no-referrer" src={attachmentSrc(m.attachment_url)} alt="" className="max-w-[240px] max-h-[240px] rounded-sm border border-slate-200" />
                                </a>
                              ) : (
                                <a href={attachmentSrc(m.attachment_url)} target="_blank" rel="noreferrer" className={`inline-flex items-center gap-1 text-[12px] underline ${mine ? "text-white" : "text-blue-700"}`}>
                                  <Download size={12} /> {m.attachment_name || "attachment"}
                                </a>
                              )}
                            </div>
                          )}
                          {m.text && (() => {
                            const xl = msgXlate[m.id];
                            const srcLang = detectLang(m.text);
                            const canTranslate = srcLang !== targetLang;
                            const showTranslated = xl?.showing && xl?.translated;
                            const displayText = showTranslated ? xl.translated : m.text;
                            return (
                              <>
                                <div className="whitespace-pre-wrap break-words">{displayText}</div>
                                {canTranslate && (
                                  <button
                                    onClick={() => translateMessage(m)}
                                    disabled={xlatingId === m.id}
                                    data-testid={`chat-translate-${m.id}`}
                                    className={`mt-1 inline-flex items-center gap-0.5 text-[10px] disabled:opacity-50 ${mine ? "text-slate-300 hover:text-white" : "text-blue-600 hover:text-blue-800"}`}
                                    title={showTranslated ? `Show original ${srcLang.toUpperCase()}` : `Translate to ${LANG_FLAG[targetLang]}`}
                                  >
                                    {xlatingId === m.id ? <RefreshCw size={9} className="animate-spin" /> : <Languages size={9} />}
                                    {showTranslated ? `Show ${LANG_FLAG[srcLang]}` : `Translate → ${LANG_FLAG[targetLang]}`}
                                  </button>
                                )}
                              </>
                            );
                          })()}
                          {mine && editingId === m.id && (
                            <div className="mt-1.5" data-testid={`chat-edit-box-${m.id}`}>
                              <textarea
                                value={editDraft}
                                onChange={(e) => setEditDraft(e.target.value)}
                                rows={2}
                                data-testid="chat-edit-input"
                                className="w-full resize-none px-2 py-1 text-[13px] rounded-sm border border-slate-300 bg-white text-slate-900 focus:outline-none focus:ring-1 focus:ring-blue-500"
                              />
                              <div className="mt-1 flex items-center gap-1.5">
                                <button
                                  onClick={() => saveEdit(m)}
                                  disabled={savingEdit}
                                  data-testid="chat-edit-save"
                                  className="px-2 py-0.5 text-[11px] rounded-sm bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                                >
                                  {savingEdit ? "Saving\u2026" : "Save"}
                                </button>
                                <button
                                  onClick={cancelEdit}
                                  data-testid="chat-edit-cancel"
                                  className="px-2 py-0.5 text-[11px] rounded-sm border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          )}
                          <div className={`mt-1 flex items-center gap-2 text-[10px] font-mono ${mine ? "text-slate-300" : "text-slate-400"}`}>
                            <span>{fmtTime(m.created_at)}</span>
                            {m.edited_at && <span data-testid={`chat-edited-${m.id}`} className="italic">edited</span>}
                            {/* Feb 26 2026 — backend attaches `can_edit` /
                                `can_delete` per message driven by the
                                admin-configured `chat_edit_window_minutes`
                                setting. Buttons hide as soon as the window
                                closes so the audit trail stays honest. */}
                            {mine && editingId !== m.id && (m.can_edit || m.can_delete) && (
                              <>
                                {m.can_edit && (
                                  <button
                                    onClick={() => startEdit(m)}
                                    data-testid={`chat-edit-btn-${m.id}`}
                                    title="Edit message"
                                    className="inline-flex items-center hover:text-white"
                                  >
                                    <Pencil size={10} />
                                  </button>
                                )}
                                {m.can_delete && (
                                  <button
                                    onClick={() => removeMessage(m)}
                                    data-testid={`chat-delete-btn-${m.id}`}
                                    title="Delete message"
                                    className="inline-flex items-center hover:text-red-400"
                                  >
                                    <Trash2 size={10} />
                                  </button>
                                )}
                              </>
                            )}
                            {mine && !m.can_edit && !m.can_delete && !m.edited_at && (
                              <span title="Edit window has expired" className="italic opacity-60" data-testid={`chat-locked-${m.id}`}>
                                locked
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {!messages.length && <div className="text-center text-slate-400 text-[12px] py-6">Say hi 👋</div>}
                </div>
                <div className="border-t border-slate-200 p-3">
                  {pending && (
                    <div className="mb-2 inline-flex items-center gap-2 px-2 py-1 border border-slate-200 rounded-sm text-[11.5px] text-slate-700 bg-slate-50">
                      <Paperclip size={11} /> {pending.name}
                      <button onClick={() => setPending(null)} className="text-slate-500 hover:text-red-600"><X size={12} /></button>
                    </div>
                  )}
                  <div className="flex items-end gap-2">
                    <input type="file" ref={fileRef} onChange={onAttach} className="hidden" />
                    <button
                      onClick={() => fileRef.current?.click()}
                      disabled={attaching}
                      data-testid="chat-attach-btn"
                      className="h-9 w-9 flex items-center justify-center border border-slate-200 rounded-sm hover:bg-slate-50 text-slate-600 disabled:opacity-50"
                      title="Attach file"
                    >
                      <Paperclip size={15} />
                    </button>
                    <textarea
                      data-testid="chat-input"
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
                      }}
                      placeholder="Type a message… (@help for AI commands • Enter to send, Shift+Enter for newline)"
                      rows={1}
                      className="flex-1 resize-none px-3 py-2 text-[13px] border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-blue-500 max-h-32"
                    />
                    <button
                      onClick={composeTranslate}
                      disabled={composeXlating || !text.trim()}
                      data-testid="chat-compose-translate"
                      title="Translate current draft (EN ↔ 中)"
                      className="h-9 px-2 inline-flex items-center gap-1 border border-indigo-200 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-sm text-[11.5px] font-semibold disabled:opacity-50 shrink-0"
                    >
                      {composeXlating ? <RefreshCw size={12} className="animate-spin" /> : <Languages size={12} />}
                      → {LANG_FLAG[detectLang(text) === "en" ? "zh" : "en"]}
                    </button>
                    <Button variant="primary" onClick={send} testId="chat-send-btn">
                      <Send size={13} /> Send
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </PageBody>

      {showNew && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40" onClick={() => setShowNew(false)}>
          <div className="w-[420px] max-w-full bg-white border border-slate-200 rounded-sm" onClick={(e) => e.stopPropagation()}>
            <div className="h-12 border-b border-slate-200 flex items-center justify-between px-4">
              <div className="font-heading text-[13.5px] font-semibold">Start conversation</div>
              <button onClick={() => setShowNew(false)} className="text-slate-500 hover:text-slate-900"><X size={15} /></button>
            </div>
            <div className="p-3 border-b border-slate-200">
              <div className="relative">
                <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  autoFocus
                  value={userQ}
                  onChange={(e) => setUserQ(e.target.value)}
                  placeholder="Search user by name or email…"
                  data-testid="chat-user-search"
                  className="h-9 w-full pl-7 pr-2 text-[13px] border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="max-h-[320px] overflow-auto">
              {filteredUsers.map((u) => (
                <button
                  key={u.id}
                  data-testid={`chat-user-${u.id}`}
                  onClick={() => openWith(u)}
                  className="w-full px-4 py-2 flex items-center gap-3 hover:bg-slate-50 text-left"
                >
                  <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-800 text-[11px] font-bold flex items-center justify-center">
                    {initials(u)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12.5px] font-medium text-slate-900 truncate">{u.name || u.email}</div>
                    <div className="text-[11px] text-slate-500 truncate">{u.email}</div>
                  </div>
                </button>
              ))}
              {!filteredUsers.length && <div className="p-4 text-center text-[12px] text-slate-500">No users found.</div>}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
